import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { HeaderBar } from '@e3dc-react/shell/Elements/Header'
import { useTypedSelector } from 'src/State/RootReducer'
import UserBadge from '../../UserBadge'
import useIsScrolled from 'src/Hooks/useIsScrolled'
import useModals from 'src/Hooks/useModals'
import { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'
import { useHeaderBreadcrumbs, useHeaderHeight, useStickyBar } from '@e3dc-react/shell/Libs/Header'
import { PageActions } from '@e3dc-react/shell/State/App/AppModel'
import { ShopBasket } from 'src/Libs/shop'
import { NotificationMenuBadge } from 'src/Libs/notifications/components'
import { isFlowTheme } from 'src/Libs/utils/theme-helpers'

interface StyleProps {
  hasStickyBar: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  header: {
    [theme.breakpoints.up('md')]: {
      borderBottomLeftRadius: props.hasStickyBar ? 0 : theme.shape.header.borderRadius,
      borderBottomRightRadius: props.hasStickyBar ? 0 : theme.shape.header.borderRadius,
    },
  },
}))

interface HeaderProps {
  onDrawerToggle: () => void
  alwaysShowTitle?: boolean
  position?: 'sticky'
  tabs: TabsBarTab[]
  activeTab: number
  customContent?: React.ReactNode
  showTitle?: boolean
  pageActions?: PageActions
}

/**
 * Header component wrapping the header bar for mobile and desktop view
 */
const Header: React.FC<HeaderProps> = ({ onDrawerToggle, alwaysShowTitle, tabs, activeTab, customContent, showTitle, pageActions }) => {
  const navigate = useNavigate()
  const title = useTypedSelector(state => state.app.title)
  const sidebarCollapsed = useTypedSelector(state => state.app.sidebarCollapsed)
  const isScrolled = useIsScrolled()
  const { openLogoutModal } = useModals()
  const { breadcrumbs } = useHeaderBreadcrumbs()
  const { setHeight } = useHeaderHeight()
  const { hasStickyBar } = useStickyBar()

  const { classes } = useStyles({ hasStickyBar })

  const onUserProfileClick = (): void => {
    navigate('/user')
  }

  const measuredRef = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const height = node.getBoundingClientRect().height

        /** since <Hidden> is deprecated and the display: 'none' does not disable the ref, we have to check if height is more that 0 */
        if (height > 0) setHeight(height)
      }
    },
    /* tabs and breadcrumbs are loaded afterwards to the node, so we need to react to them to get the correct height */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setHeight, tabs, breadcrumbs, customContent]
  )

  const customButtons = (
    <>
      <ShopBasket />
      {!isFlowTheme && <NotificationMenuBadge />}
      <UserBadge onUserProfileClick={onUserProfileClick} />
    </>
  )

  return (
    <HeaderBar
      innerRef={measuredRef}
      title={title}
      handleMenuClick={onDrawerToggle}
      handleLogoutClick={() => openLogoutModal()}
      iconBarContent={customButtons}
      withLanguageSelect={false}
      withIcons={false}
      withLanguageEditorMenu={process.env.NODE_ENV === 'development'}
      headerClassName={classes.header}
      alwaysShowTitle={alwaysShowTitle}
      collapsedSidebar={sidebarCollapsed}
      // to do remove ?? true and add smoother elevation animation with pseudo element https://tobiasahlin.com/blog/how-to-animate-box-shadow/
      elevate={isScrolled || true}
      tabs={tabs}
      activeTab={activeTab}
      data-qa="header"
      breadcrumbs={breadcrumbs}
      customContent={customContent}
      showTitle={showTitle}
      pageActions={pageActions}
    />
  )
}

export default Header
